import React, { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Select } from 'components/forms/Selects'
import { AppFrame } from 'transfix-ui/components/AppFrame'
import { Box, Typography } from 'transfix-ui/core'
import { callApi } from 'utils/api/callApi'
import { FP_ENDPOINTS, FP_HOME, FP_SUPPORT_EMAIL } from 'apps/fleet-planner/config'
import { TEAM_MEMBER_ROLE_TYPES, TEAM_MEMBER_USER_TYPES } from 'apps/fleet-planner/types'
import { IMotorCarrierJoinRequest } from 'apps/fleet-planner/types/join'
import { Button, Spinner } from 'transfix-ui'

const USER_TYPE_OPTIONS = [
  {
    label: 'Dispatcher',
    value: TEAM_MEMBER_USER_TYPES.DISPATCHER,
  },
  {
    label: 'Owner Operator',
    value: TEAM_MEMBER_USER_TYPES.OWNER_OPERATOR,
  },
]

const ROLE_OPTIONS = [
  {
    label: 'Admin',
    value: TEAM_MEMBER_ROLE_TYPES.ADMIN,
  },
  {
    label: 'Employee',
    value: TEAM_MEMBER_ROLE_TYPES.EMPLOYEE,
  },
]

const homeButton = (
  <Box display='flex' justifyContent='center' mt={3} mb={3}>
    <Link to={FP_HOME} className='btn btn-primary btn-large'>
      Return Home
    </Link>
  </Box>
)

export const JoinPage = () => {
  const { joinId } = useParams<{ joinId: string }>()
  const [userType, setUsertype] = useState<TEAM_MEMBER_USER_TYPES | undefined>(undefined)
  const [role, setRole] = useState<TEAM_MEMBER_ROLE_TYPES | undefined>(undefined)
  const [joinRequestData, setJoinRequestData] = useState<IMotorCarrierJoinRequest | undefined>(
    undefined
  )
  const [permissionDenied, setPermissionDenied] = useState<boolean>(false)

  const alreadyApproved = useMemo(() => !!joinRequestData?.approvedAt, [
    joinRequestData?.approvedAt,
  ])

  useEffect(() => {
    ;(async () => {
      try {
        const response = await callApi({
          endpoint: FP_ENDPOINTS.motorCarrierJoinRequests,
          id: joinId,
        })
        setJoinRequestData(response.motorCarrierJoinRequest)
      } catch (e) {
        setPermissionDenied(true)
      }
    })()
  }, [joinId])

  const submitHandler = () => {
    ;(async () => {
      const response = await callApi({
        endpoint: `${FP_ENDPOINTS.motorCarrierJoinRequests}/${joinId}/approve`,
        method: 'PUT',
        body: { role, userType },
      })
      setJoinRequestData(response.motorCarrierJoinRequest)
    })()
  }

  const userMessage = useMemo(() => {
    switch (userType) {
      case TEAM_MEMBER_USER_TYPES.DISPATCHER:
        return 'Dispatchers can view all of their carrier’s information and can log in to the Transfix web app.'
      case TEAM_MEMBER_USER_TYPES.OWNER_OPERATOR:
        return 'Owner Operators can view all of their carrier’s information and can log in to the Transfix web app.'
      default:
        return ''
    }
  }, [userType])

  const roleMessage = useMemo(() => {
    switch (role) {
      case TEAM_MEMBER_ROLE_TYPES.ADMIN:
        return 'Admins can view all of their carrier’s information and invite team members to the carriers they have access to.'
      case TEAM_MEMBER_ROLE_TYPES.EMPLOYEE:
        return 'Employees can view all of their carrier’s information but can’t invite team members to a carrier.'
      default:
        return ''
    }
  }, [role])

  return (
    <AppFrame>
      <Box maxWidth='768px' margin='auto' px={3} py={10}>
        {/* Approval Form */}
        {joinRequestData && !alreadyApproved && (
          <>
            {/* Heading */}
            <Box mb={3}>
              <Typography className='fs-mask' variant='h2'>
                Grant {joinRequestData.requestedBy.fullName} ({joinRequestData.requestedBy.email})
                Access to {joinRequestData.motorCarrier.name}
              </Typography>
            </Box>
            {/* User Type Select */}
            <Box mb={3}>
              <Select
                dataTest='join-select-user-type'
                id='userType'
                label='Select Their Role'
                onChange={option => setUsertype(option.value as TEAM_MEMBER_USER_TYPES)}
                options={USER_TYPE_OPTIONS}
                required
                validationMessage={userMessage}
                value={userType}
              />
            </Box>
            {/* Role Select */}
            <Box mb={3}>
              <Select
                dataTest='join-select-role'
                id='role'
                label='Select Their Permissions'
                onChange={option => setRole(option.value as TEAM_MEMBER_ROLE_TYPES)}
                options={ROLE_OPTIONS}
                required
                validationMessage={roleMessage}
                value={role}
              />
            </Box>
            {/* Submit Button */}
            <Box display='flex' justifyContent='flex-end' mb={3}>
              <Button
                color='primary'
                onClick={submitHandler}
                size='large'
                data-test='join-submit'
                disabled={!role || !userType}>
                Grant Access
              </Button>
            </Box>
          </>
        )}
        {/* Approval Notification */}
        {joinRequestData && alreadyApproved && (
          <>
            <Typography variant='h2'>
              {joinRequestData.requestedBy.fullName} ({joinRequestData.requestedBy.email}) has
              access to {joinRequestData.motorCarrier.name}
            </Typography>
            {homeButton}
          </>
        )}
        {/* Permission Denied */}
        {permissionDenied && (
          <>
            <Typography variant='h2'>Unable to approve carrier join request</Typography>
            The carrier join request cannot be found or the current account does not have
            persmission to approve it. <br />
            <a href={`mailto:${FP_SUPPORT_EMAIL}`}>Contact us</a> if you need help.
            {homeButton}
          </>
        )}
        {/* Loading */}
        {!joinRequestData && !permissionDenied && <Spinner />}
      </Box>
    </AppFrame>
  )
}
